'use client';

import type { ReactNode } from 'react';
import { Add01Icon, Delete01Icon, Edit03Icon } from '@terra/ui-icons';
import { hasAllowlistPathRules } from '@terra/shared-contracts-applications';
import { applicationDomainScopeSwitchId } from '@terra/shared-utils';
import type { TableGridRow } from '../table-grid';
import { DomainScopeSwitch } from './DomainScopeSwitch';
import { PrimaryDomainCell } from './PrimaryDomainCell';
import {
  buildActionsCell,
  buildDomainPathCell,
  buildExpandCell,
  buildPathScopeBadgeCell,
  DomainScopeTableActionButton,
  TruncatedCellText,
} from './domain-scope-table-cells';
import {
  applyPathFunctionalityToggle,
  applyPathTestingToggle,
  applyPathThirdPartyToggle,
  getPathEntries,
  getPathScopeDisplayState,
  getPathScopeFlag,
  getScopeFlag,
  isFunctionalityToggleDisabled,
  type DomainConfig,
  type PathScopeEntry,
  type PathScopeKind,
} from './domain-scope.utils';

export type DomainScopePathRowLabels = {
  manual: string;
  enabled: string;
  disabled: string;
  allowlist: string;
  blocklist: string;
  neverTested: string;
  editPath: string;
  deleteLabel: string;
};

const buildPathNameCell = ({
  path,
  kind,
}: {
  path: string;
  kind: PathScopeEntry['kind'];
}) => (
  <TruncatedCellText
    text={path}
    pl="8"
    color={kind === 'blocklist' ? 'fg.error' : 'fg.default'}
  />
);

export const buildPathRowCells = ({
  labels,
  domainName,
  pathEntry,
  isReadOnly,
  isPrimaryHost,
  onUpdateDomain,
  onEditPath,
  onRemovePath,
}: {
  labels: DomainScopePathRowLabels;
  domainName: string;
  pathEntry: PathScopeEntry;
  isReadOnly?: boolean;
  isPrimaryHost?: boolean;
  onUpdateDomain: (
    domainName: string,
    updater: (domain: DomainConfig) => DomainConfig,
  ) => void;
  onEditPath?: (params: {
    path: string;
    kind: PathScopeKind;
    isInFunctionalityScope: boolean;
    isInTestingScope: boolean;
    isThirdParty: boolean;
  }) => void;
  onRemovePath: (path: string) => void;
}): ReactNode[] => {
  const { path, kind } = pathEntry;
  const pathFunctionalityOn = getPathScopeFlag({
    rule: pathEntry,
    field: 'functionality',
  });
  const pathTestingOn = getPathScopeFlag({
    rule: pathEntry,
    field: 'testing',
  });
  const pathThirdPartyOn = getPathScopeFlag({
    rule: pathEntry,
    field: 'thirdParty',
  });
  const thirdPartyDisabled = isReadOnly || Boolean(isPrimaryHost);

  return [
    null,
    buildPathNameCell({ path, kind }),
    labels.manual,
    kind === 'allowlist' ? (
      <DomainScopeSwitch
        checked={pathThirdPartyOn}
        disabled={thirdPartyDisabled}
        tooltip={pathThirdPartyOn ? labels.enabled : labels.disabled}
        onCheckedChange={() =>
          onUpdateDomain(domainName, (current) =>
            applyPathThirdPartyToggle({
              domain: current,
              path,
              enabled: !pathThirdPartyOn,
            }),
          )
        }
        dataTestid={applicationDomainScopeSwitchId({
          field: 'thirdParty',
          host: domainName,
          path,
        })}
      />
    ) : null,
    kind === 'allowlist' ? (
      <DomainScopeSwitch
        checked={pathFunctionalityOn}
        disabled={
          isReadOnly || isFunctionalityToggleDisabled({ rule: pathEntry })
        }
        tooltip={pathFunctionalityOn ? labels.enabled : labels.disabled}
        onCheckedChange={() =>
          onUpdateDomain(domainName, (current) =>
            applyPathFunctionalityToggle({
              domain: current,
              path,
              enabled: !pathFunctionalityOn,
            }),
          )
        }
        dataTestid={applicationDomainScopeSwitchId({
          field: 'interaction',
          host: domainName,
          path,
        })}
      />
    ) : null,
    kind === 'blocklist' ? (
      <TruncatedCellText text={labels.neverTested} color="fg.muted" />
    ) : (
      <DomainScopeSwitch
        checked={pathTestingOn}
        disabled={isReadOnly}
        tooltip={pathTestingOn ? labels.enabled : labels.disabled}
        onCheckedChange={() =>
          onUpdateDomain(domainName, (current) =>
            applyPathTestingToggle({
              domain: current,
              path,
              enabled: !pathTestingOn,
            }),
          )
        }
        dataTestid={applicationDomainScopeSwitchId({
          field: 'attack',
          host: domainName,
          path,
        })}
      />
    ),
    buildPathScopeBadgeCell({
      variant: kind === 'allowlist' ? 'allowlist' : 'blocklist',
      text: kind === 'allowlist' ? labels.allowlist : labels.blocklist,
    }),
    !isReadOnly
      ? buildActionsCell(
          <>
            <DomainScopeTableActionButton
              label={labels.deleteLabel}
              onClick={() => onRemovePath(path)}
            >
              <Delete01Icon size={16} />
            </DomainScopeTableActionButton>
            <DomainScopeTableActionButton
              label={labels.editPath}
              onClick={() =>
                onEditPath?.({
                  path,
                  kind,
                  isInFunctionalityScope: pathFunctionalityOn,
                  isInTestingScope: pathTestingOn,
                  isThirdParty: pathThirdPartyOn,
                })
              }
            >
              <Edit03Icon size={16} />
            </DomainScopeTableActionButton>
          </>,
        )
      : null,
  ];
};

type PathToRemove = {
  domain: string;
  path: string;
};
type DomainScopeTableRowLabels = {
  automatic: string;
  manual: string;
  enabled: string;
  disabled: string;
  configuredPerPath: string;
  allPaths: string;
  partially: string;
  allowlist: string;
  blocklist: string;
  neverTested: string;
  addPaths: string;
  editPath: string;
  deleteLabel: string;
  primary: string;
  redirectsTo: (domain: string) => string;
  primaryInteractionAlwaysOn: string;
};

export type BuildDomainScopeTableRowsParams = {
  labels: DomainScopeTableRowLabels;
  secondaryDomains: DomainConfig[];
  discoveredSubdomains: string[];
  trimmedPrimaryApplicationUrl: string;
  primaryHostname: string | null;
  primaryHostDomainEntry?: DomainConfig;
  redirectTargetHostname: string | null;
  primaryPathEntries: PathScopeEntry[];
  primaryAttackEnabled: boolean;
  isPrimaryExpanded: boolean;
  expandedDomains: Set<string>;
  isReadOnly?: boolean;
  renderDomainSuffix?: (domain: string) => ReactNode;
  onTogglePrimaryExpanded: () => void;
  onToggleDomainExpanded: (domain: string) => void;
  onPrimaryAttackToggle: () => void;
  onFunctionalityToggle: (domain: string) => void;
  onTestingToggle: (domain: string) => void;
  onThirdPartyToggle: (domain: string) => void;
  onAddPrimaryPaths: () => void;
  onRemovePrimaryPath: (path: string) => void;
  onEditPrimaryPath: (params: {
    path: string;
    kind: PathScopeKind;
    isInFunctionalityScope: boolean;
    isInTestingScope: boolean;
    isThirdParty: boolean;
  }) => void;
  onUpdatePrimaryHostPaths: (
    updater: (domain: DomainConfig) => DomainConfig,
  ) => void;
  onOpenPathsDialog: (params: {
    domain: string;
    editPath?: string;
    editKind?: PathScopeKind;
    editIsInFunctionalityScope?: boolean;
    editIsInTestingScope?: boolean;
    editIsThirdParty?: boolean;
  }) => void;
  onRemoveDomain: (domain: string) => void;
  onRequestRemovePath: (target: PathToRemove) => void;
  onUpdateDomain: (
    domainName: string,
    updater: (domain: DomainConfig) => DomainConfig,
  ) => void;
};

export function buildDomainScopeTableRows({
  labels,
  secondaryDomains,
  discoveredSubdomains,
  trimmedPrimaryApplicationUrl,
  primaryHostname,
  primaryHostDomainEntry,
  redirectTargetHostname,
  primaryPathEntries,
  primaryAttackEnabled,
  isPrimaryExpanded,
  expandedDomains,
  isReadOnly,
  renderDomainSuffix,
  onTogglePrimaryExpanded,
  onToggleDomainExpanded,
  onPrimaryAttackToggle,
  onFunctionalityToggle,
  onTestingToggle,
  onThirdPartyToggle,
  onAddPrimaryPaths,
  onRemovePrimaryPath,
  onEditPrimaryPath,
  onUpdatePrimaryHostPaths,
  onOpenPathsDialog,
  onRemoveDomain,
  onRequestRemovePath,
  onUpdateDomain,
}: BuildDomainScopeTableRowsParams): TableGridRow[] {
  const rows: TableGridRow[] = [];

  if (trimmedPrimaryApplicationUrl !== '' && primaryHostname) {
    const primaryPathScopeState = getPathScopeDisplayState(
      primaryHostDomainEntry ?? {
        domain: primaryHostname,
        source: 'USER_PROVIDED',
        isInFunctionalityScope: true,
        isInTestingScope: primaryAttackEnabled,
        isThirdParty: false,
        pathRules: [],
        hasWaf: null,
        createdAt: new Date(),
        enabledAt: null,
      },
    );
    const usesAllowlistPartialScope = hasAllowlistPathRules(
      primaryHostDomainEntry?.pathRules,
    );
    const canExpandPrimary = primaryPathScopeState === 'partial';
    const primaryGroupId = `primary-${primaryHostname}`;
    const primarySearchPaths = primaryPathEntries.map(
      (pathEntry) => pathEntry.path,
    );

    rows.push({
      id: `primary-${primaryHostname}`,
      groupId: primaryGroupId,
      value: [
        primaryHostname,
        trimmedPrimaryApplicationUrl,
        redirectTargetHostname,
        ...primarySearchPaths,
      ]
        .filter(Boolean)
        .join(' '),
      cells: [
        buildExpandCell({
          canExpand: canExpandPrimary,
          isExpanded: isPrimaryExpanded,
          onToggle: onTogglePrimaryExpanded,
        }),
        <PrimaryDomainCell
          key="primary-domain"
          domain={primaryHostname}
          primaryLabel={labels.primary}
          redirectTargetLabel={
            redirectTargetHostname
              ? labels.redirectsTo(redirectTargetHostname)
              : undefined
          }
          suffix={renderDomainSuffix?.(primaryHostname)}
        />,
        labels.manual,
        <DomainScopeSwitch
          checked={false}
          disabled
          tooltip={labels.disabled}
          onCheckedChange={() => undefined}
          dataTestid={applicationDomainScopeSwitchId({
            field: 'thirdParty',
            host: primaryHostname,
          })}
        />,
        <DomainScopeSwitch
          key="primary-functionality"
          checked
          disabled
          tooltip={labels.primaryInteractionAlwaysOn}
          onCheckedChange={() => undefined}
          dataTestid={applicationDomainScopeSwitchId({
            field: 'interaction',
            host: primaryHostname,
          })}
        />,
        usesAllowlistPartialScope ? null : (
          <DomainScopeSwitch
            checked={primaryAttackEnabled}
            disabled={isReadOnly}
            tooltip={primaryAttackEnabled ? labels.enabled : labels.disabled}
            onCheckedChange={onPrimaryAttackToggle}
            dataTestid={applicationDomainScopeSwitchId({
              field: 'attack',
              host: primaryHostname,
            })}
          />
        ),
        primaryPathScopeState === 'all'
          ? buildPathScopeBadgeCell({
              variant: 'allPaths',
              text: labels.allPaths,
            })
          : buildPathScopeBadgeCell({
              variant: 'partially',
              text: labels.partially,
            }),
        !isReadOnly
          ? buildActionsCell(
              <DomainScopeTableActionButton
                label={labels.addPaths}
                onClick={onAddPrimaryPaths}
              >
                <Add01Icon size={14} />
              </DomainScopeTableActionButton>,
            )
          : null,
      ],
    });
    if (isPrimaryExpanded) {
      primaryPathEntries.forEach((pathEntry) => {
        rows.push({
          id: `primary-${pathEntry.kind}-${pathEntry.path}`,
          groupId: primaryGroupId,
          value: [primaryHostname, pathEntry.path].join(' '),
          variant: 'subtle',
          cells: buildPathRowCells({
            labels,
            domainName: primaryHostname,
            pathEntry,
            isReadOnly,
            isPrimaryHost: true,
            onUpdateDomain: (_, updater) => onUpdatePrimaryHostPaths(updater),
            onEditPath: onEditPrimaryPath,
            onRemovePath: onRemovePrimaryPath,
          }),
        });
      });
    }
  }

  secondaryDomains.forEach((entry) => {
    const isExpanded = expandedDomains.has(entry.domain);
    const pathScopeState = getPathScopeDisplayState(entry);
    const canExpand = pathScopeState === 'partial';
    const usesAllowlistPartialScope = hasAllowlistPathRules(entry.pathRules);
    const functionalityOn = getScopeFlag({
      domain: entry,
      field: 'functionality',
    });
    const testingOn = getScopeFlag({ domain: entry, field: 'testing' });
    const thirdPartyOn = getScopeFlag({ domain: entry, field: 'thirdParty' });
    const isAutomatic = discoveredSubdomains.includes(entry.domain);
    const domainPathEntries = getPathEntries(entry);
    const domainGroupId = entry.domain;

    rows.push({
      id: entry.domain,
      groupId: domainGroupId,
      value: [
        entry.domain,
        ...domainPathEntries.map((pathEntry) => pathEntry.path),
      ].join(' '),
      cells: [
        buildExpandCell({
          canExpand,
          isExpanded,
          onToggle: () => onToggleDomainExpanded(entry.domain),
        }),
        buildDomainPathCell({
          domain: entry.domain,
          text: entry.domain,
          renderDomainSuffix,
        }),
        isAutomatic ? labels.automatic : labels.manual,
        usesAllowlistPartialScope ? null : (
          <DomainScopeSwitch
            checked={thirdPartyOn}
            disabled={isReadOnly}
            tooltip={thirdPartyOn ? labels.enabled : labels.disabled}
            onCheckedChange={() => onThirdPartyToggle(entry.domain)}
            dataTestid={applicationDomainScopeSwitchId({
              field: 'thirdParty',
              host: entry.domain,
            })}
          />
        ),
        <DomainScopeSwitch
          checked={functionalityOn}
          disabled={
            isReadOnly || isFunctionalityToggleDisabled({ domain: entry })
          }
          tooltip={functionalityOn ? labels.enabled : labels.disabled}
          onCheckedChange={() => onFunctionalityToggle(entry.domain)}
          dataTestid={applicationDomainScopeSwitchId({
            field: 'interaction',
            host: entry.domain,
          })}
        />,
        usesAllowlistPartialScope ? null : (
          <DomainScopeSwitch
            checked={testingOn}
            disabled={isReadOnly}
            tooltip={testingOn ? labels.enabled : labels.disabled}
            onCheckedChange={() => onTestingToggle(entry.domain)}
            dataTestid={applicationDomainScopeSwitchId({
              field: 'attack',
              host: entry.domain,
            })}
          />
        ),
        pathScopeState === 'all'
          ? buildPathScopeBadgeCell({
              variant: 'allPaths',
              text: labels.allPaths,
            })
          : buildPathScopeBadgeCell({
              variant: 'partially',
              text: labels.partially,
            }),
        !isReadOnly
          ? buildActionsCell(
              <>
                <DomainScopeTableActionButton
                  label={labels.deleteLabel}
                  onClick={() => onRemoveDomain(entry.domain)}
                >
                  <Delete01Icon size={16} />
                </DomainScopeTableActionButton>
                <DomainScopeTableActionButton
                  label={labels.addPaths}
                  onClick={() => onOpenPathsDialog({ domain: entry.domain })}
                >
                  <Add01Icon size={14} />
                </DomainScopeTableActionButton>
              </>,
            )
          : null,
      ],
    });
    if (isExpanded) {
      domainPathEntries.forEach((pathEntry) => {
        rows.push({
          id: `${entry.domain}-${pathEntry.kind}-${pathEntry.path}`,
          groupId: domainGroupId,
          value: [entry.domain, pathEntry.path].join(' '),
          variant: 'subtle',
          cells: buildPathRowCells({
            labels,
            domainName: entry.domain,
            pathEntry,
            isReadOnly,
            onUpdateDomain,
            onEditPath: ({
              path,
              kind,
              isInFunctionalityScope,
              isInTestingScope,
              isThirdParty,
            }) =>
              onOpenPathsDialog({
                domain: entry.domain,
                editPath: path,
                editKind: kind,
                editIsInFunctionalityScope: isInFunctionalityScope,
                editIsInTestingScope: isInTestingScope,
                editIsThirdParty: isThirdParty,
              }),
            onRemovePath: (path) =>
              onRequestRemovePath({
                domain: entry.domain,
                path,
              }),
          }),
        });
      });
    }
  });

  return rows;
}
